import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import QRCode from 'react-qr-code';
import { Button, Input, ModalWindow, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { DeploymentUtils, useParticipantWebAppUrl } from 'libs.react_internal';
import { LogEvents } from '../../../constants/log_events.constants';
import { getPatientAppNonWebUrls } from '../../../helpers/study.helpers';
import { useCopyToClipboard } from '../../../hooks/use_copy_to_clipboard';
import { useCurrentEnvironment } from '../../../hooks/use_current_environment';
export const PatientAppInfoPopup = ({ isOpen, onClose, onLogEvent }) => {
    const translate = useLocalizeMessage();
    const [, copy] = useCopyToClipboard();
    const { environment, baseCortexURL, selectedLane, laneCount } = useCurrentEnvironment();
    const { addNotification } = useToastNotification();
    // Resolve study URL from the base (non-lane) host; the returned URL is never lane-specific, so
    // getLaneUrl below applies the active lane the way the deployed app generation expects.
    const { participantWebAppUrl } = useParticipantWebAppUrl({
        cortexUrl: baseCortexURL ?? environment?.cortexURL,
        code: environment?.code,
    });
    const laneAwareParticipantWebAppUrl = useMemo(() => {
        if (!participantWebAppUrl) {
            return '';
        }
        if (laneCount <= 1) {
            return participantWebAppUrl;
        }
        return DeploymentUtils.getLaneUrl(participantWebAppUrl, selectedLane);
    }, [participantWebAppUrl, laneCount, selectedLane]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const contents = useMemo(() => {
        let paiOSUrl = '';
        let paAndroidUrl = '';
        const cortexUrlForAppStores = baseCortexURL ?? environment?.cortexURL;
        if (cortexUrlForAppStores) {
            const nonWebUrls = getPatientAppNonWebUrls(cortexUrlForAppStores);
            paiOSUrl = nonWebUrls.ios;
            paAndroidUrl = nonWebUrls.android;
        }
        return [
            {
                type: 'pa-web',
                title: translate('Medable for participants - Web URL'),
                url: laneAwareParticipantWebAppUrl,
            },
            {
                type: 'pa-ios',
                title: translate('Medable for participants - iOS App'),
                url: paiOSUrl,
            },
            {
                type: 'pa-android',
                title: translate('Medable for participants - Android App'),
                url: paAndroidUrl,
            },
        ];
    }, [baseCortexURL, environment?.cortexURL, laneAwareParticipantWebAppUrl, translate]);
    const currentContent = useMemo(() => contents[currentIndex], [contents, currentIndex]);
    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? contents.length - 1 : prev - 1));
    };
    const handleNext = () => {
        setCurrentIndex((prev) => (prev === contents.length - 1 ? 0 : prev + 1));
    };
    const handleCopyToClipboard = (url) => {
        copy(url);
        onLogEvent(LogEvents.PATIENT_APP_URL_COPIED, { copiedUrl: url });
        addNotification({
            title: translate('Medable for participants URL copied to clipboard'),
            type: 'success',
        });
    };
    return (_jsx(ModalWindow, { title: translate('Medable for participants'), isOpen: isOpen, closeWindow: onClose, hideCloseIcon: false, footerClassNames: 'hidden', children: _jsxs("div", { className: 'flex flex-col items-center gap-4 justify-between h-full', children: [_jsx(Text, { className: 'text-lg font-bold w-full text-left', children: currentContent.title }), _jsxs("div", { className: 'w-full', children: [_jsxs("div", { className: 'flex items-center justify-between gap-4 w-full', children: [_jsx(Button, { size: 'xs', label: '', onClick: handlePrevious, iconName: 'chevronLeft', variant: 'secondary', disabled: contents.length <= 1 }), _jsx(QRCode, { value: currentContent.url, size: 400, level: 'H', "data-testid": 'qr-code-svg', "data-value": currentContent.url }), _jsx(Button, { size: 'xs', label: '', onClick: handleNext, iconName: 'chevronRight', variant: 'secondary', disabled: contents.length <= 1 })] }), contents.length > 1 && (_jsx("div", { className: 'flex items-center justify-center gap-2 mt-4', children: contents.map((content, index) => (_jsx("button", { onClick: () => setCurrentIndex(index), className: `w-2 h-2 rounded-full transition-colors ${index === currentIndex ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'}`, "aria-label": `Go to slide ${index + 1}` }, content.type))) }))] }), _jsxs("div", { className: 'w-full flex items-center justify-space-between gap-2', children: [_jsx("div", { className: 'w-full', children: _jsx(Input, { value: currentContent.url, id: `${currentContent.type}-url-input`, readOnly: true, width: 'full', type: 'text' }) }), _jsx(Button, { label: '', onClick: () => handleCopyToClipboard(currentContent.url), iconName: 'clipboardDocumentCheck' })] })] }) }));
};
